import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";
import { api } from "../lib/api";
import { MotorRecalc } from "./MotorRecalc";
import { AppText, Badge, Button, Card } from "./ui";

const INPUT = "min-h-[44px] bg-white rounded-xl border-2 border-km-border px-3 outline-none focus:border-km-coral font-text text-[15px] w-full";

function TableField({ f, value, onChange }) {
  const rows = value || [];
  const set = (ri, col, v) => onChange(rows.map((r, i) => (i === ri ? { ...r, [col]: v } : r)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]">
        <thead><tr><th className="text-left py-1 pr-2" />{f.cols.map(([k, l]) => <th key={k} className="text-left py-1 px-1 font-bold text-km-muted">{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={r.row} className="border-t border-km-divider">
              <td className="py-1 pr-2 font-bold whitespace-nowrap">{(f.rows.find((x) => x[0] === r.row) || [r.row, r.row])[1]}</td>
              {f.cols.map(([k]) => (
                <td key={k} className="py-1 px-1"><input type="number" step="any" value={r[k] ?? ""} onChange={(e) => set(ri, k, e.target.value)} data-testid={`rule-${f.key}-${r.row}-${k}`} className="min-h-[40px] w-24 bg-white rounded-lg border-2 border-km-border px-2 outline-none focus:border-km-coral" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RuleField({ f, value, onChange }) {
  const modified = JSON.stringify(value) !== JSON.stringify(f.default);
  return (
    <div className="flex flex-col gap-1.5 border-t border-km-divider pt-3" data-testid={`rule-field-${f.key}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <AppText variant="bodyStrong">{f.label}</AppText>
        {modified ? <Badge text="modifié" tone="brand" /> : null}
      </div>
      {f.help ? <AppText variant="caption" className="text-km-muted">{f.help}</AppText> : null}
      {f.type === "table" ? <TableField f={f} value={value} onChange={onChange} /> : null}
      {["int", "float", "percent"].includes(f.type) ? <input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`rule-${f.key}`} className={INPUT + " max-w-[160px]"} /> : null}
      {f.type === "text" ? <textarea rows={Math.min(6, Math.max(2, Math.ceil(String(value || "").length / 90)))} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`rule-${f.key}`} className={INPUT + " py-2"} /> : null}
      {f.type === "lines" || f.type === "ints" ? <textarea rows={Math.max(2, (value || []).length + 1)} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={`rule-${f.key}`} className={INPUT + " py-2"} placeholder="une valeur par ligne" /> : null}
      {f.type === "ints" ? <AppText variant="caption" className="text-km-muted">Un nombre par ligne, dans l'ordre des paliers.</AppText> : null}
    </div>
  );
}

function RuleSection({ section, onSaved }) {
  const [vals, setVals] = useState(() => Object.fromEntries(section.fields.map((f) => [f.key, f.value])));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVals(Object.fromEntries(section.fields.map((f) => [f.key, f.value]))); }, [section]);
  const dirty = section.fields.some((f) => JSON.stringify(vals[f.key]) !== JSON.stringify(f.value));
  const save = async () => {
    setBusy(true);
    try {
      const changed = Object.fromEntries(section.fields.filter((f) => JSON.stringify(vals[f.key]) !== JSON.stringify(f.value)).map((f) => [f.key, vals[f.key]]));
      const r = await api.coachSaveRules(changed);
      toast.success("Règles enregistrées — appliquées aux prochaines séances et semaines générées");
      onSaved(r);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const resetAll = async () => {
    if (!window.confirm("Rétablir les valeurs d'origine de cette section ?")) return;
    setBusy(true);
    try { onSaved(await api.coachResetRules(section.fields.map((f) => f.key))); toast.success("Section rétablie"); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-3 p-4" testID={`rules-section-${section.key}`}>
      <AppText variant="subtitle">{section.label}</AppText>
      {section.fields.map((f) => <RuleField key={f.key} f={f} value={vals[f.key]} onChange={(v) => setVals((s) => ({ ...s, [f.key]: v }))} />)}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button title="Enregistrer" size="md" icon={<Save size={16} strokeWidth={2.5} />} onClick={save} loading={busy} disabled={!dirty} testID={`rules-save-${section.key}`} />
        <Button title="Valeurs d'origine" size="md" variant="secondary" icon={<RotateCcw size={16} strokeWidth={2.5} />} onClick={resetAll} disabled={busy} testID={`rules-reset-${section.key}`} />
      </div>
    </Card>
  );
}

export function CoachRules() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.coachRules().then(setData).catch((e) => setError(e.message)); }, []);
  if (error) return <AppText variant="body" className="text-km-coral">{error}</AppText>;
  if (!data) return <div className="py-12 flex justify-center"><span className="h-8 w-8 animate-spin rounded-full border-4 border-km-coral border-t-transparent" /></div>;
  return (
    <div className="flex flex-col gap-4" data-testid="coach-rules">
      <Card className="p-4 bg-km-sunny/40">
        <AppText variant="bodyStrong">La grille de l'entraîneur, en direct</AppText>
        <AppText variant="caption" className="text-km-ontertiary mt-1">Chaque valeur ici est une règle du moteur (grille v2.1). Une modification s'applique immédiatement aux prochaines séances et semaines générées — les séances déjà écrites ne bougent pas. « Valeurs d'origine » remet la grille validée. La logique (ordre des séances, espacement, transitions) reste codée : pour la changer, on en parle.</AppText>
      </Card>
      {data.sections.map((s) => <React.Fragment key={s.key}><RuleSection section={s} onSaved={setData} />{s.key === "moteur" ? <MotorRecalc /> : null}</React.Fragment>)}
    </div>
  );
}
